import React, { Component } from "react";
import { toastManager } from "../../services/toastManager";
import { useApiRequests } from "../../services/fetchApiServices/index";
import { CustomModal, LoadingSpinner } from "../../common";
import "./styles.scss";

export class ModalAddProduct extends Component {
  constructor(props) {
    super(props);
    this.state = {
      newProductName: "",
      newProductPrice: 0,
      newProductComment: "",
      isNewProductAvailable: false,
      isSecondModalOpen: false,
      isLoading: false,
      isDragging: false,
      selectedFiles: [], // Array to store multiple new files
      imagePreviewUrl: "",
      previewImages: [], // Array to store all preview images
      currentImageIndex: 0, // Index of currently displayed image
      isZoomed: false, // State for zoom modal
    };
    this.fileInputRef = React.createRef();
  }

  componentWillUnmount() {
    // Clean up blob URLs to prevent memory leaks
    this.state.previewImages.forEach(img => {
      if (img.url && img.url.startsWith('blob:')) {
        URL.revokeObjectURL(img.url);
      }
    });
  }

  // Generic navigation helper method
  navigateImage = (direction) => {
    const { previewImages, currentImageIndex } = this.state;
    if (previewImages.length === 0) return;

    let newIndex;
    if (direction === 'previous') {
      newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : previewImages.length - 1;
    } else if (direction === 'next') {
      newIndex = currentImageIndex < previewImages.length - 1 ? currentImageIndex + 1 : 0;
    } else {
      return; // Invalid direction
    }

    this.setState({
      currentImageIndex: newIndex,
      imagePreviewUrl: previewImages[newIndex].url
    });
  };

  // Navigation functions for image carousel
  goToPreviousImage = () => {
    this.navigateImage('previous');
  };

  goToNextImage = () => {
    this.navigateImage('next');
  };

  // Zoom functionality
  openZoom = () => {
    this.setState({ isZoomed: true });
  };

  closeZoom = () => {
    this.setState({ isZoomed: false });
  };

  goToPreviousImageZoomed = () => {
    this.navigateImage('previous');
  };

  goToNextImageZoomed = () => {
    this.navigateImage('next');
  };

  onNewProductNameChange = (event) => {
    this.setState({ newProductName: event.target.value });
  };

  onNewProductPriceChange = (event) => {
    this.setState({ newProductPrice: parseFloat(event.target.value) });
  };

  onNewProductCommentChange = (event) => {
    this.setState({ newProductComment: event.target.value });
  };


  onIsNewProductAvailableChange = (event) => {
    this.setState({ isNewProductAvailable: event.target.value === "Yes" });
  };

  handleOpenConfirmationModal = () => {
    this.setState({ isSecondModalOpen: true });
  };

  handleConfirmAddProduct = async () => {
    this.setState({ isSecondModalOpen: false, isLoading: true });
    await this.handleAddNewProduct();
    this.setState({ isLoading: false });
    this.props.handleModalClose();
  };
  // Helper method to upload files for a product
  uploadProductFiles = async (apiRequests, productId) => {
    if (this.state.selectedFiles.length === 0 || !productId) {
      return;
    }

    // Upload each file sequentially
    for (const file of this.state.selectedFiles) {
      await apiRequests.uploadFile(file, productId);
    }
  };

  // Helper method to create product
  createProduct = async (apiRequests) => {
    return await apiRequests.postAddNewProducs(
      this.state.newProductName,
      this.state.newProductPrice,
      this.state.newProductComment,
      this.state.isNewProductAvailable
    );
  };

  handleAddNewProduct = async () => {
    try {
      const apiRequests = useApiRequests();
      
      // Create the product
      const productResponse = await this.createProduct(apiRequests);

      if (productResponse.status === 200) {
        const productId = productResponse.data.productId;
        
        // Upload files if any are selected
        await this.uploadProductFiles(apiRequests, productId);
        
        // Refresh the data
        this.props.fetchData();
      } else {
        throw new Error('Failed to create product');
      }
    } catch (error) {
      console.log(error.message);
    }
  };
  resetPreviewState = (images = [], files = [], index = 0, previewUrl = "") => {
    this.setState({
      selectedFiles: files,
      imagePreviewUrl: previewUrl,
      previewImages: images,
      currentImageIndex: index,
    });
  };

  handleResetForm = () => {
    // Clean up preview URLs
    this.state.previewImages.forEach(img => {
      if (img.url && img.url.startsWith('blob:')) {
        URL.revokeObjectURL(img.url);
      }
    });
    
    this.setState({
      newProductName: "",
      newProductPrice: 0,
      newProductComment: "",
      isNewProductAvailable: false,
    });

    // Reset preview state using helper method
    this.resetPreviewState();
  };

  handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: true });
  };

  handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: false });
  };

  handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  // Helper method to validate and filter image files
  validateAndFilterImageFiles = (files) => {
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    let message = null;
    let success = true;

    if (imageFiles.length === 0) {
      success = false;
      message = 'Please drop at least one image file.';
    } else if (imageFiles.length !== files.length) {
      const skippedCount = files.length - imageFiles.length;
      message = `${skippedCount} non-image files were skipped. Only image files are allowed.`;
      // Optionally, you can alert here or let the caller handle the message
    }

    return { success, imageFiles, message };
  };

  handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragging: false });

    const files = Array.from(e.dataTransfer.files);
    const validation = this.validateAndFilterImageFiles(files);
    
    if (!validation.success) {
      toastManager.warn(validation.message);
      return;
    }

    this.handleMultipleFileSelection(validation.imageFiles);
  };

  handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    const validation = this.validateAndFilterImageFiles(files);
    if (!validation.success) {
      toastManager.warn(validation.message || 'Please select at least one image file.');
      e.target.value = '';
      return;
    }
    
    this.handleMultipleFileSelection(validation.imageFiles);
    
    // Clear the input so the same files can be selected again
    e.target.value = '';
  };

  // Helper method to create image preview object
  createImagePreview = (file) => {
    const previewUrl = URL.createObjectURL(file);
    return {
      url: previewUrl,
      name: file.name,
      size: file.size,
      file: file // Store the actual file for uploading later
    };
  };

  handleFileSelection = (file) => {
    const newImagePreview = this.createImagePreview(file);
    
    // Add the new image to the preview images array and set it as current
    const updatedImages = [...this.state.previewImages, newImagePreview];
    const newCurrentIndex = updatedImages.length - 1; // Set to the new image
    
    // Add the file to selectedFiles array
    const updatedSelectedFiles = [...this.state.selectedFiles, file];
    
    this.setState({ 
      selectedFiles: updatedSelectedFiles, 
      imagePreviewUrl: newImagePreview.url,
      previewImages: updatedImages,
      currentImageIndex: newCurrentIndex
    });
  };

  handleMultipleFileSelection = (files) => {
    // Process all files at once to avoid state conflicts
    const newImagePreviews = files.map(file => this.createImagePreview(file));
    
    // Update state with all new images at once
    const updatedImages = [...this.state.previewImages, ...newImagePreviews];
    const updatedSelectedFiles = [...this.state.selectedFiles, ...files];
    
    // Set the current image to the first newly added image
    const newCurrentIndex = this.state.previewImages.length; // Index of first new image
    
    this.setState({
      selectedFiles: updatedSelectedFiles,
      previewImages: updatedImages,
      currentImageIndex: newCurrentIndex,
      imagePreviewUrl: newImagePreviews[0].url
    });
  };

  // Helper method to render form fields
  renderFormFields = () => {
    return (
      <>
        <div className="form-group">
          <label
            htmlFor="name"
            className="col-xl-4 col-sm-5 col-xs-5 control-label"
          >
            Product Name:
          </label>
          <div className="col-12">
            <input
              className="form-control"
              name="name"
              type="text"
              id="name"
              value={this.state.newProductName}
              onChange={this.onNewProductNameChange}
              placeholder="Product Name"
            />
          </div>
        </div>

        <div className="form-group">
          <label
            htmlFor="price"
            className="col-xl-4 col-sm-5 col-xs-5 control-label"
          >
            Price:
          </label>
          <div className="col-12">
            <input
              className="form-control"
              name="price"
              id="price"
              type="number"
              value={this.state.newProductPrice}
              onChange={this.onNewProductPriceChange}
              placeholder="Price"
            />
          </div>
        </div>

        <div className="form-group">
          <label
            htmlFor="comment"
            className="col-xl-4 col-sm-5 col-xs-5 control-label"
          >
            Comment:
          </label>
          <div className="col-12">
            <textarea
              className="form-control"
              name="comment"
              id="comment"
              value={this.state.newProductComment}
              onChange={this.onNewProductCommentChange}
              placeholder="Comment"
              rows="8"
              style={{ resize: 'none' }}
            />
          </div>
        </div>

        <div className="form-group">
          <label
            htmlFor="availability"
            className="col-xl-4 col-sm-5 col-xs-5 control-label"
          >
            Is product available?:
          </label>
          <div className="col-12">
            <select
              className="form-control"
              onChange={this.onIsNewProductAvailableChange}
              value={this.state.isNewProductAvailable ? "Yes" : "No"}
            >
              <option>No</option>
              <option>Yes</option>
            </select>
          </div>
        </div>
      </>
    );
  };

  // Helper method to render image navigation controls
  renderImageNavigation = () => {
    if (this.state.previewImages.length <= 1) return null;

    return (
      <>
        <button
          className="nav-arrow nav-arrow-left"
          onClick={(e) => {
            e.stopPropagation();
            this.goToPreviousImage();
          }}
        >
          ‹
        </button>
        <button
          className="nav-arrow nav-arrow-right"
          onClick={(e) => {
            e.stopPropagation();
            this.goToNextImage();
          }}
        >
          ›
        </button>
        <div className="image-counter">
          {this.state.currentImageIndex + 1} / {this.state.previewImages.length}
        </div>
      </>
    );
  };

  // Helper method to handle image removal
  handleImageRemoval = (e) => {
    e.stopPropagation();
    const currentImage = this.state.previewImages[this.state.currentImageIndex];
    
    if (currentImage) {
      // Clean up the blob URL
      if (currentImage.url && currentImage.url.startsWith('blob:')) {
        URL.revokeObjectURL(currentImage.url);
      }
      
      // Remove from both arrays
      const updatedImages = this.state.previewImages.filter((_, index) => index !== this.state.currentImageIndex);
      const updatedSelectedFiles = this.state.selectedFiles.filter(file => file !== currentImage.file);
      
      if (updatedImages.length > 0) {
        const newIndex = this.state.currentImageIndex >= updatedImages.length ? 0 : this.state.currentImageIndex;
        this.resetPreviewState(updatedImages, updatedSelectedFiles, newIndex, updatedImages[newIndex].url);
      } else {
        this.resetPreviewState([], updatedSelectedFiles);
      }
    }
  };

  // Helper method to render image preview content
  renderImagePreview = () => {
    if (!this.state.imagePreviewUrl) {
      return (
        <div className="upload-prompt">
          <i className="upload-icon">📁</i>
          <p>
            Drag and drop images here or click to select multiple files
          </p>
        </div>
      );
    }

    return (
      <div className="image-preview">
        <img
          src={this.state.imagePreviewUrl}
          alt="Product preview"
          onClick={(e) => {
            e.stopPropagation();
            this.openZoom();
          }}
          style={{ cursor: 'pointer' }}
        />
        
        {/* Image navigation controls */}
        {this.renderImageNavigation()}
        
        {/* Remove button */}
        <button
          className="remove-image"
          onClick={this.handleImageRemoval}
        >
          &times;
        </button>
      </div>
    );
  };

  // Helper method to render drag and drop zone
  renderDragDropZone = () => {
    const { isDragging } = this.state;

    return (
      <div className="form-group">
        <label
          htmlFor="image"
          className="col-xl-4 col-sm-5 col-xs-5 control-label"
        >
          Product Image:
        </label>
        <div className="col-12">
          <div
            className={`drag-drop-zone ${isDragging ? "dragging" : ""}`}
            onDragEnter={this.handleDragEnter}
            onDragLeave={this.handleDragLeave}
            onDragOver={this.handleDragOver}
            onDrop={this.handleDrop}
            onClick={() => this.fileInputRef.current.click()}
          >
            {this.renderImagePreview()}
            <input
              ref={this.fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={this.handleFileSelect}
            />
          </div>
          
          {/* Add Images Button */}
          <div className="add-images-button-container" style={{ textAlign: 'left', marginTop: '10px' }}>
            <button
              type="button"
              className="btn btn-secondary add-images-btn"
              onClick={() => this.fileInputRef.current.click()}
              style={{ fontSize: '12px'}}
            >
              <i className="upload-icon">📁</i>
              Add Images
            </button>
          </div>
          
          {this.state.isLoading && <LoadingSpinner />}
        </div>
      </div>
    );
  };

  // Helper method to render modal footer
  renderModalFooter = () => {
    const isFormValid = this.state.newProductName.length && this.state.newProductPrice > 0;
    
    return (
      <div className="modal-footer">
        <button
          type="button"
          className="btn"
          onClick={this.handleResetForm}
        >
          Reset
        </button>
        <button
          type="button"
          className="btn"
          onClick={this.props.handleModalClose}
        >
          Close
        </button>
        <button
          type="button"
          className={isFormValid ? "btn btn-primary" : "btn disabled"}
          onClick={this.handleOpenConfirmationModal}
          disabled={!isFormValid}
        >
          Add Product
        </button>
      </div>
    );
  };

  // Helper method to render zoom modal
  renderZoomModal = () => {
    if (!this.state.isZoomed) return null;

    return (
      <div className="zoom-modal" onClick={this.closeZoom}>
        <div className="zoom-content" onClick={(e) => e.stopPropagation()}>
          <button className="zoom-close" onClick={this.closeZoom}>
            &times;
          </button>
          
          <img
            src={this.state.imagePreviewUrl}
            alt="Product preview zoomed"
            className="zoomed-image"
          />
          
          {/* Navigation controls for zoom */}
          {this.state.previewImages.length > 1 && (
            <>
              <button
                className="zoom-nav zoom-nav-left"
                onClick={this.goToPreviousImageZoomed}
              >
                ‹
              </button>
              <button
                className="zoom-nav zoom-nav-right"
                onClick={this.goToNextImageZoomed}
              >
                ›
              </button>
              <div className="zoom-counter">
                {this.state.currentImageIndex + 1} / {this.state.previewImages.length}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  render() {
    return (
      <>
        <div
          id="helpModal"
          className="modal fade in example-modal"
          tabIndex="-1"
          role="dialog"
          aria-labelledby="modal-title-2"
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <button
                  type="button"
                  onClick={this.props.handleModalClose}
                  className="close"
                  aria-label="Close"
                >
                  &times;
                </button>
                <h4 id="modal-title-2" className="modal-title">
                  Add product
                </h4>
              </div>
              <div className="modal-body">
                {this.renderFormFields()}
                {this.renderDragDropZone()}
              </div>
              {this.renderModalFooter()}
            </div>
          </div>
        </div>

        {this.state.isSecondModalOpen && (
          <CustomModal
            handleModalClose={this.props.handleModalClose}
            handleSubmitForm={this.handleConfirmAddProduct}
            isModalOpen={this.state.isSecondModalOpen}
            message={`Do you want to add new product: "${this.state.newProductName}"?`}
          />
        )}
        {this.state.isLoading && <LoadingSpinner />}
        {this.renderZoomModal()}
      </>
    );
  }
}